import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';
import { getStripePromise } from '../utils/stripeLoader';

/**
 * 创建支付会话
 * @param {Object} paymentData - 支付数据
 * @param {string} paymentData.sessionId - 会话ID (可选)
 * @param {string} paymentData.professionalId - 专业人员ID (可选)
 * @param {number} paymentData.amount - 金额 (可选)
 * @param {string} paymentData.currency - 货币代码 (默认: gbp)
 * @param {string} paymentData.description - 描述 (可选)
 * @returns {Promise} 支付会话响应
 */
export const createPaymentSession = async (paymentData) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.post(getApiUrl('/payments/create-session'), paymentData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error creating payment session:', error);
    throw error;
  }
};

/**
 * 重定向到Stripe支付页面
 * @param {string} sessionId - Stripe会话ID
 */
export const redirectToStripeCheckout = async (sessionId) => {
  try {
    const stripePromise = await getStripePromise();
    const stripe = await stripePromise;

    if (!stripe) {
      throw new Error('Stripe failed to load');
    }

    const { error } = await stripe.redirectToCheckout({
      sessionId: sessionId
    });

    if (error) {
      console.error('Error redirecting to checkout:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error redirecting to Stripe checkout:', error);
    throw error;
  }
};

/**
 * 处理支付流程 (创建会话并重定向)
 * @param {Object} paymentData - 支付数据
 * @returns {Promise} 支付处理结果
 */
export const processPayment = async (paymentData) => {
  try {
    // 创建支付会话
    const sessionResponse = await createPaymentSession(paymentData);
    
    if (!sessionResponse.success) {
      throw new Error(sessionResponse.error?.message || 'Failed to create payment session');
    }

    // 重定向到Stripe支付页面
    await redirectToStripeCheckout(sessionResponse.data.sessionId);
    
    return sessionResponse.data;
  } catch (error) {
    console.error('Error processing payment:', error);
    throw error;
  }
};

/**
 * 创建预授权付款
 * @param {Object} paymentData - 支付数据
 * @param {string} paymentData.professionalId - 专业人员ID
 * @param {number} paymentData.amount - 金额
 * @param {string} paymentData.currency - 货币代码
 * @param {string} paymentData.description - 描述
 * @param {Object} paymentData.sessionDetails - 会话详情
 * @returns {Promise} 预授权付款响应
 */
export const createAuthorizationPayment = async (paymentData) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    console.log('🔵 Creating payment authorization:', {
      url: getApiUrl('/payments/create-authorization'),
      paymentData: {
        professionalId: paymentData.professionalId,
        amount: paymentData.amount,
        currency: paymentData.currency,
        hasSessionDetails: !!paymentData.sessionDetails
      }
    });

    const response = await axios.post(getApiUrl('/payments/create-authorization'), paymentData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('✅ Payment authorization created successfully:', {
      success: response.data.success,
      hasPaymentIntentId: !!response.data.data?.paymentIntentId
    });

    return response.data;
  } catch (error) {
    console.error('❌ Error creating authorization payment:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      statusText: error.response?.statusText
    });

    // Re-throw with more detailed error information
    const enhancedError = new Error(
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      error.message ||
      'Failed to create payment authorization'
    );
    enhancedError.originalError = error;
    enhancedError.statusCode = error.response?.status;
    enhancedError.details = error.response?.data?.error;
    throw enhancedError;
  }
};

/**
 * 确认预授权付款
 * @param {Object} confirmData - 确认数据
 * @param {string} confirmData.paymentIntentId - 付款意向ID
 * @param {string} confirmData.clientSecret - 客户端密钥
 * @returns {Promise} 确认付款响应
 */
export const confirmAuthorizationPayment = async (confirmData) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required');
    }

    console.log('🌐 Making API call to confirm-authorization:', {
      url: getApiUrl('/payments/confirm-authorization'),
      data: confirmData,
      hasToken: !!token
    });

    const response = await axios.post(getApiUrl('/payments/confirm-authorization'), confirmData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('🌐 API response received:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error confirming authorization payment:', error);
    throw error;
  }
};

/**
 * 获取支付历史
 * @param {Object} options - 查询选项
 * @param {number} options.page - 页码
 * @param {number} options.limit - 每页数量
 * @param {string} options.status - 支付状态筛选
 * @returns {Promise} 支付历史响应
 */
export const getPaymentHistory = async (options = {}) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required');
    }

    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    if (options.status) params.append('status', options.status);

    const response = await axios.get(getApiUrl(`/payments/history?${params}`), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching payment history:', error);
    throw error;
  }
};

/**
 * 获取支付详情
 * @param {string} paymentId - 支付ID
 * @returns {Promise} 支付详情响应
 */
export const getPaymentDetails = async (paymentId) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.get(getApiUrl(`/payments/${paymentId}`), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching payment details:', error);
    throw error;
  }
};

/**
 * 格式化金额显示
 * @param {number} amount - 金额（分）
 * @param {string} currency - 货币代码
 * @returns {string} 格式化后的金额
 */
export const formatAmount = (amount, currency = 'gbp') => {
  const formattedAmount = (amount / 100).toFixed(2);
  
  const currencySymbols = {
    'gbp': '£',
    'cny': '¥',
    'usd': '$',
    'eur': '€'
  };

  const symbol = currencySymbols[currency.toLowerCase()] || currency.toUpperCase();
  
  return `${symbol}${formattedAmount}`;
};

/**
 * 获取支付状态的中文显示
 * @param {string} status - 支付状态
 * @returns {string} 中文状态
 */
export const getPaymentStatusText = (status) => {
  const statusMap = {
    'pending': '待支付',
    'succeeded': '支付成功',
    'failed': '支付失败',
    'refunded': '已退款'
  };

  return statusMap[status] || status;
};

/**
 * 获取支付状态的颜色
 * @param {string} status - 支付状态
 * @returns {string} 状态对应的颜色
 */
export const getPaymentStatusColor = (status) => {
  const colorMap = {
    'pending': 'warning',
    'succeeded': 'success',
    'failed': 'error',
    'refunded': 'info'
  };

  return colorMap[status] || 'default';
};

export default {
  createPaymentSession,
  redirectToStripeCheckout,
  processPayment,
  createAuthorizationPayment,
  confirmAuthorizationPayment,
  getPaymentHistory,
  getPaymentDetails,
  formatAmount,
  getPaymentStatusText,
  getPaymentStatusColor
}; 